import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { UniversalEntity } from '../../../../shared/src/models/universal_entity';
import { entityService } from '../services/entity/entityService';

interface EntityContextType {
    currentEntity: UniversalEntity | null;
    setCurrentEntity: (entity: UniversalEntity | null) => void;
    isLoading: boolean;
    refreshEntity: () => Promise<void>;
}

const EntityContext = createContext<EntityContextType | undefined>(undefined);

export function EntityProvider({ children }: { children: ReactNode }) {
    const [currentEntity, setCurrentEntity] = useState<UniversalEntity | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    // In a real app, this would load the last selected entity from local storage or user prefs
    useEffect(() => {
        async function loadInitialEntity() {
            setIsLoading(true);
            try {
                const clientId = 'current-client-id'; // This would come from AuthContext
                const entities = await entityService.listEntities(clientId);
                if (entities.length > 0) {
                    // Ideally check localStorage for last active entity ID
                    setCurrentEntity(entities[0]);
                }
            } catch (error) {
                console.error("Failed to load entities", error);
            } finally {
                setIsLoading(false);
            }
        }

        loadInitialEntity();
    }, []);

    const refreshEntity = async () => {
        if (currentEntity) {
            const refreshed = await entityService.getEntity(currentEntity.id);
            if (refreshed) setCurrentEntity(refreshed);
        }
    };

    return (
        <EntityContext.Provider value={{ currentEntity, setCurrentEntity, isLoading, refreshEntity }}>
            {children}
        </EntityContext.Provider>
    );
}

export function useEntity() {
    const context = useContext(EntityContext);
    if (context === undefined) {
        throw new Error('useEntity must be used within an EntityProvider');
    }
    return context;
}
